import { types as appTypes } from "../app/App.redux";
import { types as loginTypes } from "../login/Login.redux";
import addLastModifiedDateObject from "./helpers/add-last-modified-date-object";
import getFilterOptions from "./helpers/get-filter-options";
import getFilteredPhotos from "./helpers/get-filtered-photos";
import getPagedFiles from "./helpers/get-paged-files";
import getSortedPhotos from "./helpers/get-sorted-photos";

export const SELECT_FILES_LIMIT = 15;

/**
 * Action types
 * @property {string} SELECT_FILES_ACTIVE_TAB
 * @property {string} SELECT_FILES_CHOOSE
 * @property {string} SELECT_FILES_CHOOSE_CLEAR
 * @property {string} SELECT_FILES_SOURCE
 * @property {string} SELECT_FILES_PHOTOS_PAGE
 * @property {string} SELECT_FILES_PHOTOS_INTERNAL_FILTER
 * @property {string} SELECT_FILES_PHOTOS_INTERNAL_PAGE
 * @property {string} SELECT_FILES_PHOTOS_INTERNAL_SORT
 */
export const types = {
  SELECT_FILES_ACTIVE_TAB: "SELECT_FILES_ACTIVE_TAB",
  SELECT_FILES_CHOOSE: "SELECT_FILES_CHOOSE",
  SELECT_FILES_CHOOSE_CLEAR: "SELECT_FILES_CHOOSE_CLEAR",
  SELECT_FILES_SOURCE: "SELECT_FILES_SOURCE",
  SELECT_FILES_PHOTOS_PAGE: "SELECT_FILES_PHOTOS_PAGE",
  SELECT_FILES_PHOTOS_INTERNAL_FILTER: "SELECT_FILES_PHOTOS_INTERNAL_FILTER",
  SELECT_FILES_PHOTOS_INTERNAL_PAGE: "SELECT_FILES_PHOTOS_INTERNAL_PAGE",
  SELECT_FILES_PHOTOS_INTERNAL_SORT: "SELECT_FILES_PHOTOS_INTERNAL_SORT",
};

/**
 * @namespace Reducers
 */
export const reducers = {
  /**
   * Updates select files listing state
   * @param {string} state
   * @param {object} action
   * @param {string} action.type
   * @param {string} action.activeListing
   * @memberof Reducers
   */
  selectFilesActiveListing: (state = "", action) => {
    switch (action.type) {
      case types.SELECT_FILES_SOURCE:
        return action.activeListing;
      case appTypes.ROUTE_CHANGE:
      case loginTypes.AUTH_CLEARED:
        return "";
      default:
        return state;
    }
  },

  /**
   * Updates select files active tab state
   * @param {number} state
   * @param {object} action
   * @param {string} action.type
   * @param {number} action.value
   * @memberof Reducers
   */
  selectFilesActiveTab: (state = 0, action) => {
    switch (action.type) {
      case types.SELECT_FILES_ACTIVE_TAB:
        return action.value;
      case appTypes.ROUTE_CHANGE:
      case loginTypes.AUTH_CLEARED:
        return 0;
      default:
        return state;
    }
  },

  /**
   * Updates select files attachments state
   * @param {array} state
   * @param {object} action
   * @param {string} action.type
   * @param {array} action.attachments
   * @memberof Reducers
   */
  selectFilesAttachments: (state = [], action) => {
    switch (action.type) {
      case types.SELECT_FILES_SOURCE:
        return action.attachments;
      case appTypes.ROUTE_CHANGE:
      case loginTypes.AUTH_CLEARED:
        return [];
      default:
        return state;
    }
  },

  /**
   * Updates select files chosen state
   * @param {array} state
   * @param {object} action
   * @param {string} action.type
   * @param {array} action.chosen
   * @memberof Reducers
   */
  selectFilesChosen: (state = [], action) => {
    switch (action.type) {
      case types.SELECT_FILES_CHOOSE:
        return action.chosen;
      case types.SELECT_FILES_CHOOSE_CLEAR:
      case appTypes.ROUTE_CHANGE:
      case loginTypes.AUTH_CLEARED:
        return [];
      default:
        return state;
    }
  },

  /**
   * Updates select files photos state
   * @param {array} state
   * @param {object} action
   * @param {string} action.type
   * @param {array} action.photos
   * @memberof Reducers
   */
  selectFilesPhotos: (state = [], action) => {
    switch (action.type) {
      case types.SELECT_FILES_SOURCE:
        return action.photos;
      case appTypes.ROUTE_CHANGE:
      case loginTypes.AUTH_CLEARED:
        return [];
      default:
        return state;
    }
  },

  /**
   * Updates select files photos page state
   * @param {number} state
   * @param {object} action
   * @param {string} action.type
   * @param {number} action.value
   * @memberof Reducers
   */
  selectFilesPhotosPage: (state = 1, action) => {
    switch (action.type) {
      case types.SELECT_FILES_PHOTOS_PAGE:
        return action.value;
      case appTypes.ROUTE_CHANGE:
      case loginTypes.AUTH_CLEARED:
        return 1;
      default:
        return state;
    }
  },

  /**
   * Updates select files photos page count state
   * @param {number} state
   * @param {object} action
   * @param {string} action.type
   * @param {number} action.count
   * @memberof Reducers
   */
  selectFilesPhotosPageCount: (state = 0, action) => {
    switch (action.type) {
      case types.SELECT_FILES_PHOTOS_PAGE:
        return action.count;
      case appTypes.ROUTE_CHANGE:
      case loginTypes.AUTH_CLEARED:
        return 0;
      default:
        return state;
    }
  },

  /**
   * Updates select files paged photos state
   * @param {string} state
   * @param {object} action
   * @param {string} action.type
   * @param {array} action.photos
   * @memberof Reducers
   */
  selectFilesPhotosPaged: (state = [], action) => {
    switch (action.type) {
      case types.SELECT_FILES_PHOTOS_PAGE:
        return action.photos;
      case appTypes.ROUTE_CHANGE:
      case loginTypes.AUTH_CLEARED:
        return [];
      default:
        return state;
    }
  },

  /**
   * Updates select files photos page count state
   * @param {number} state
   * @param {object} action
   * @param {string} action.type
   * @param {number} action.limit
   * @memberof Reducers
   */
  selectFilesPhotosPageLimit: (state = 12, action) => {
    switch (action.type) {
      case types.SELECT_FILES_PHOTOS_PAGE:
        return action.limit;
      case appTypes.ROUTE_CHANGE:
      case loginTypes.AUTH_CLEARED:
        return 12;
      default:
        return state;
    }
  },

  /**
   * Updates select files internal photos state
   * @param {array} state
   * @param {object} action
   * @param {string} action.type
   * @param {array} action.photosInternal
   * @memberof Reducers
   */
  selectFilesPhotosInternal: (state = [], action) => {
    switch (action.type) {
      case types.SELECT_FILES_SOURCE:
        return action.photosInternal;
      case appTypes.ROUTE_CHANGE:
      case loginTypes.AUTH_CLEARED:
        return [];
      default:
        return state;
    }
  },

  /**
   * Updates select files filtered internal photo state
   * @param {string} state
   * @param {object} action
   * @param {string} action.type
   * @param {array} action.photos
   * @memberof Reducers
   */
  selectFilesPhotosInternalFiltered: (state = [], action) => {
    switch (action.type) {
      case types.SELECT_FILES_PHOTOS_INTERNAL_FILTER:
        return action.photos;
      case appTypes.ROUTE_CHANGE:
      case loginTypes.AUTH_CLEARED:
        return [];
      default:
        return state;
    }
  },

  /**
   * Updates select files internal photo filter options state
   * @param {object} state
   * @param {array} state.descriptions
   * @param {array} state.sources
   * @param {array} state.taskNames
   * @param {object} action
   * @param {string} action.type
   * @param {array} action.photosInternal
   * @memberof Reducers
   */
  selectFilesPhotosInternalFilterOptions: (
    state = { descriptions: [], sources: [], taskNames: [] },
    action
  ) => {
    switch (action.type) {
      case types.SELECT_FILES_SOURCE:
        return action.photosInternalFilterOptions;
      case appTypes.ROUTE_CHANGE:
      case loginTypes.AUTH_CLEARED:
        return { descriptions: [], sources: [], taskNames: [] };
      default:
        return state;
    }
  },

  /**
   * Updates select files internal photo description filter state
   * @param {string} state
   * @param {object} action
   * @param {string} action.type
   * @param {string} action.filter
   * @param {string} action.value
   * @param {boolean} action.global
   * @memberof Reducers
   */
  selectFilesPhotosInternalFilterDescription: (state = "", action) => {
    switch (action.type) {
      case types.SELECT_FILES_PHOTOS_INTERNAL_FILTER:
        if (action.filter === "description") {
          return action.value;
        } else if (action.global) {
          return "";
        } else {
          return state;
        }
      case appTypes.ROUTE_CHANGE:
      case loginTypes.AUTH_CLEARED:
        return "";
      default:
        return state;
    }
  },

  /**
   * Updates select files internal photo global filter state
   * @param {string} state
   * @param {object} action
   * @param {string} action.type
   * @param {string} action.filter
   * @param {string} action.value
   * @param {boolean} action.global
   * @memberof Reducers
   */
  selectFilesPhotosInternalFilterGlobal: (state = "", action) => {
    switch (action.type) {
      case types.SELECT_FILES_PHOTOS_INTERNAL_FILTER:
        return action.global ? action.value : "";
      case appTypes.ROUTE_CHANGE:
      case loginTypes.AUTH_CLEARED:
        return "";
      default:
        return state;
    }
  },

  /**
   * Updates select files internal photo source filter state
   * @param {string} state
   * @param {object} action
   * @param {string} action.type
   * @param {string} action.filter
   * @param {string} action.value
   * @param {boolean} action.global
   * @memberof Reducers
   */
  selectFilesPhotosInternalFilterSource: (state = "", action) => {
    switch (action.type) {
      case types.SELECT_FILES_PHOTOS_INTERNAL_FILTER:
        if (action.filter === "source") {
          return action.value;
        } else if (action.global) {
          return "";
        } else {
          return state;
        }
      case appTypes.ROUTE_CHANGE:
      case loginTypes.AUTH_CLEARED:
        return "";
      default:
        return state;
    }
  },

  /**
   * Updates select files internal photo task name filter state
   * @param {string} state
   * @param {object} action
   * @param {string} action.type
   * @param {string} action.filter
   * @param {string} action.value
   * @param {boolean} action.global
   * @memberof Reducers
   */
  selectFilesPhotosInternalFilterTaskName: (state = "", action) => {
    switch (action.type) {
      case types.SELECT_FILES_PHOTOS_INTERNAL_FILTER:
        if (action.filter === "taskName") {
          return action.value;
        } else if (action.global) {
          return "";
        } else {
          return state;
        }
      case appTypes.ROUTE_CHANGE:
      case loginTypes.AUTH_CLEARED:
        return "";
      default:
        return state;
    }
  },

  /**
   * Updates select files internal photos page state
   * @param {number} state
   * @param {object} action
   * @param {string} action.type
   * @param {number} action.value
   * @memberof Reducers
   */
  selectFilesPhotosInternalPage: (state = 1, action) => {
    switch (action.type) {
      case types.SELECT_FILES_PHOTOS_INTERNAL_PAGE:
        return action.value;
      case appTypes.ROUTE_CHANGE:
      case loginTypes.AUTH_CLEARED:
        return 1;
      default:
        return state;
    }
  },

  /**
   * Updates select files internal photos page count state
   * @param {number} state
   * @param {object} action
   * @param {string} action.type
   * @param {number} action.count
   * @memberof Reducers
   */
  selectFilesPhotosInternalPageCount: (state = 0, action) => {
    switch (action.type) {
      case types.SELECT_FILES_PHOTOS_INTERNAL_PAGE:
        return action.count;
      case appTypes.ROUTE_CHANGE:
      case loginTypes.AUTH_CLEARED:
        return 0;
      default:
        return state;
    }
  },

  /**
   * Updates select files paged internal photos state
   * @param {string} state
   * @param {object} action
   * @param {string} action.type
   * @param {array} action.photos
   * @memberof Reducers
   */
  selectFilesPhotosInternalPaged: (state = [], action) => {
    switch (action.type) {
      case types.SELECT_FILES_PHOTOS_INTERNAL_PAGE:
        return action.photos;
      case appTypes.ROUTE_CHANGE:
      case loginTypes.AUTH_CLEARED:
        return [];
      default:
        return state;
    }
  },

  /**
   * Updates select files internal photos page count state
   * @param {number} state
   * @param {object} action
   * @param {string} action.type
   * @param {number} action.limit
   * @memberof Reducers
   */
  selectFilesPhotosInternalPageLimit: (state = 12, action) => {
    switch (action.type) {
      case types.SELECT_FILES_PHOTOS_INTERNAL_PAGE:
        return action.limit;
      case appTypes.ROUTE_CHANGE:
      case loginTypes.AUTH_CLEARED:
        return 12;
      default:
        return state;
    }
  },

  /**
   * Updates select files internal photo sort state
   * @param {string} state
   * @param {object} action
   * @param {string} action.type
   * @param {string} action.value
   * @memberof Reducers
   */
  selectFilesPhotosInternalSort: (state = "newest", action) => {
    switch (action.type) {
      case types.SELECT_FILES_PHOTOS_INTERNAL_SORT:
        return action.value;
      case appTypes.ROUTE_CHANGE:
      case loginTypes.AUTH_CLEARED:
        return "newest";
      default:
        return state;
    }
  },

  /**
   * Updates select files sorted internal photos state
   * @param {string} state
   * @param {object} action
   * @param {string} action.type
   * @param {array} action.photos
   * @memberof Reducers
   */
  selectFilesPhotosInternalSorted: (state = [], action) => {
    switch (action.type) {
      case types.SELECT_FILES_PHOTOS_INTERNAL_SORT:
        return action.photos;
      case appTypes.ROUTE_CHANGE:
      case loginTypes.AUTH_CLEARED:
        return [];
      default:
        return state;
    }
  },
};

/**
 * @namespace Actions
 */
export const actions = {
  /**
   * Clear all chosen
   * @memberof Actions
   */
  clearChosen: () => ({
    type: types.SELECT_FILES_CHOOSE_CLEAR,
  }),

  /**
   * Set chosen
   * @param {string} fileName
   * @param {fileType} fileType
   * @memberof Actions
   */
  setChosen:
    ({ fileName, ...rest }) =>
    (dispatch, getState) => {
      let { selectFilesChosen: chosen } = getState();
      let shouldAdd = chosen.length < SELECT_FILES_LIMIT;

      // Remove file if already chosen
      chosen = chosen.filter(file => {
        if (file.fileName === fileName) {
          shouldAdd = false;
          return false;
        } else {
          return true;
        }
      });

      // Otherwise add file
      if (shouldAdd) {
        chosen.push({ fileName, ...rest });
      }

      return dispatch({
        type: types.SELECT_FILES_CHOOSE,
        chosen,
      });
    },

  /**
   * Set chosen batch
   * @param {array} files
   * @param {("add" | "remove")} action
   * @memberof Actions
   */
  setChosenBatch:
    (files, action = "add") =>
    (dispatch, getState) => {
      let { selectFilesChosen: chosen } = getState();

      const fileNames = files.reduce((names, file) => {
        if (!!file.fileName) {
          names.push(file.fileName);
        }

        return names;
      }, []);

      chosen = chosen.filter(file => !fileNames.includes(file.fileName));

      if (action === "add") {
        chosen = [...chosen, ...files].slice(0, SELECT_FILES_LIMIT);
      }

      dispatch({
        type: types.SELECT_FILES_CHOOSE,
        chosen,
      });
    },

  /**
   * Set chosen files from saved state
   * I.E., clicking an edit product sheet link from an email
   *
   * @param {object} savedState
   * @param {array} savedState.photos
   * @param {array} savedState.attachments
   * @memberof Actions
   */
  setChosenSavedState: savedState => (dispatch, getState) => {
    const { photos: savedPhotos = [], attachments: savedAttachments = [] } =
      savedState || {};
    const {
      selectFilesPhotos,
      selectFilesPhotosInternal,
      selectFilesAttachments,
    } = getState();

    const photos = [...selectFilesPhotos, ...selectFilesPhotosInternal]
      .filter(({ liveCropped, original }) =>
        savedPhotos.some(photo => photo === liveCropped || photo === original)
      )
      .map(({ lastModifiedObj, liveCropped, original }) => ({
        fileDate: lastModifiedObj,
        fileId: liveCropped || original,
        fileIndex: 0,
        fileIsPublic: !!liveCropped,
        fileName: liveCropped || original,
        fileTitle: liveCropped || original,
        fileType: "photo",
      }));

    const attachments = selectFilesAttachments
      .filter(({ id }) =>
        savedAttachments.some(attachment => `${attachment}` === `${id}`)
      )
      .map(attachment => ({
        fileDate: attachment.reportDate,
        fileId: attachment.id,
        fileIndex: 0,
        fileIsPublic: attachment.isPublic,
        fileName: attachment.fileName,
        fileTitle: attachment.name,
        fileType: "attachment",
      }));

    const chosenBatch = [...photos, ...attachments].slice(
      0,
      SELECT_FILES_LIMIT
    );

    if (!!chosenBatch.length) {
      dispatch(actions.setChosenBatch(chosenBatch));
    }
  },

  /**
   * Set source
   * @param {string} activeListing
   * @param {array} attachments
   * @param {array} photos
   * @param {array} photosInternal
   * @memberof Actions
   */
  setSource:
    (activeListing, attachments, photos, photosInternal) => async dispatch => {
      await Promise.all([
        dispatch({
          type: types.SELECT_FILES_SOURCE,
          activeListing,
          attachments,
          photos: addLastModifiedDateObject(photos),
          photosInternal: addLastModifiedDateObject(photosInternal),
          photosInternalFilterOptions: getFilterOptions(photosInternal),
        }),
      ]);

      dispatch(actions.setPhotosPage());
      dispatch(actions.setPhotosInternalFilter());

      return;
    },

  /**
   * Set select photos internal page
   * @param {string} value
   * @memberof Actions
   */
  setPhotosPage:
    (value = 1, limit) =>
    (dispatch, getState) => {
      const { selectFilesPhotos, selectFilesPhotosPageLimit } = getState();

      if (!limit) {
        limit = selectFilesPhotosPageLimit;
      }

      const { paged, pageCount } = getPagedFiles(
        selectFilesPhotos,
        value,
        limit
      );

      dispatch({
        type: types.SELECT_FILES_PHOTOS_PAGE,
        value,
        count: pageCount,
        limit,
        photos: paged,
      });
    },

  /**
   * Set select files internal photos filter
   * @param {string} filter
   * @param {string} value
   * @param {boolean} global
   * @memberof Actions
   */
  setPhotosInternalFilter:
    (filter = "", value = "", global = false) =>
    async (dispatch, getState) => {
      const { selectFilesPhotosInternal } = getState();

      await Promise.all([
        dispatch({
          type: types.SELECT_FILES_PHOTOS_INTERNAL_FILTER,
          filter,
          value,
          global,
          photos: getFilteredPhotos(filter, value, selectFilesPhotosInternal),
        }),
      ]);

      return dispatch(actions.setPhotosInternalSort());
    },

  /**
   * Set select files internal photos page
   * @param {string} value
   * @memberof Actions
   */
  setPhotosInternalPage:
    (value = 1, limit) =>
    (dispatch, getState) => {
      const {
        selectFilesPhotosInternalSorted,
        selectFilesPhotosInternalPageLimit,
      } = getState();

      if (!limit) {
        limit = selectFilesPhotosInternalPageLimit;
      }

      const { paged, pageCount } = getPagedFiles(
        selectFilesPhotosInternalSorted,
        value,
        limit
      );

      dispatch({
        type: types.SELECT_FILES_PHOTOS_INTERNAL_PAGE,
        value,
        count: pageCount,
        limit,
        photos: paged,
      });
    },

  /**
   * Set select files internal sort
   * @param {string} value
   * @memberof Actions
   */
  setPhotosInternalSort:
    (value = "newest") =>
    async (dispatch, getState) => {
      const { selectFilesPhotosInternalFiltered } = getState();

      await Promise.all([
        dispatch({
          type: types.SELECT_FILES_PHOTOS_INTERNAL_SORT,
          value,
          photos: getSortedPhotos(selectFilesPhotosInternalFiltered, value),
        }),
      ]);

      return dispatch(actions.setPhotosInternalPage());
    },

  /**
   * Set active tab
   * @param {number} value
   * @memberof Actions
   */
  setTab: (value = 0) => ({
    type: types.SELECT_FILES_ACTIVE_TAB,
    value,
  }),
};
